import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import localization from 'assets/localization/localization';
import DropDownInput from '../Input/dropDownInput/dropDownInput';
import ConstantInput from '../Input/constantInput/constantInput';
import { fetchSalesTargetPeriods } from 'services/api/salesTargetsApi';

const MONTHS = [
  { id: '1', name: 'January' },
  { id: '2', name: 'February' },
  { id: '3', name: 'March' },
  { id: '4', name: 'April' },
  { id: '5', name: 'May' },
  { id: '6', name: 'June' },
  { id: '7', name: 'July' },
  { id: '8', name: 'August' },
  { id: '9', name: 'September' },
  { id: '10', name: 'October' },
  { id: '11', name: 'November' },
  { id: '12', name: 'December' },
];

const MONTH_NAMES_SHORT = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

const YEAR_START = 2026;

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = YEAR_START; y <= currentYear + 2; y += 1) {
    years.push({ id: String(y), name: String(y) });
  }
  return years;
};

const formatShortYmd = (value) => {
  if (!value) return '';
  try {
    const raw = String(value).trim();
    const date = /^\d{4}-\d{2}-\d{2}/.test(raw)
      ? new Date(`${raw.slice(0, 10)}T12:00:00`)
      : new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.getDate();
    const monthShort = MONTH_NAMES_SHORT[date.getMonth() + 1];
    return `${day} ${monthShort} ${date.getFullYear()}`;
  } catch {
    return '';
  }
};

const formatPeriodOptionLabel = (period) => {
  const monthName =
    MONTHS.find((m) => m.id === String(period.period_month))?.name || period.period_month;
  const year = period.period_year;
  const start = formatShortYmd(period.period_start);
  const end = period.period_end ? formatShortYmd(period.period_end) : 'Present';
  const range = start ? `${start} – ${end}` : '';
  return range ? `${monthName} ${year} (${range})` : `${monthName} ${year}`;
};

const INITIAL_MONTHLY_FILTER = {
  period_id: '',
  month: '',
  year: '',
  search: '',
};

const INITIAL_WEEKLY_FILTER = {
  period_id: '',
  search: '',
};

const INITIAL_ADDITIONAL_FILTER = {
  period_id: '',
  month: '',
  year: '',
  search: '',
};

const SalesTargetsFilter = ({ applyFilter, listFilterData }) => {
  const listType = listFilterData?.listType || 'monthly';
  const isWeekly = listType === 'weekly';
  const isAdditional = listType === 'additional';
  const appliedValues = listFilterData?.appliedFilterValues;
  const [filterFieldsData, setFilterFieldsData] = useState(
    isWeekly
      ? INITIAL_WEEKLY_FILTER
      : isAdditional
        ? INITIAL_ADDITIONAL_FILTER
        : INITIAL_MONTHLY_FILTER
  );
  const [periodOptions, setPeriodOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { results } = await fetchSalesTargetPeriods(1, 50);
        if (cancelled) return;
        setPeriodOptions(
          (results || []).map((period) => ({
            id: String(period.id),
            name: formatPeriodOptionLabel(period),
            raw: period,
          }))
        );
      } catch {
        if (!cancelled) setPeriodOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isWeekly) {
      setFilterFieldsData({
        period_id: appliedValues?.period_id ? String(appliedValues.period_id) : '',
        search: appliedValues?.search ?? '',
      });
      return;
    }
    setFilterFieldsData({
      period_id: appliedValues?.period_id ? String(appliedValues.period_id) : '',
      month: appliedValues?.month ?? '',
      year: appliedValues?.year ?? '',
      search: appliedValues?.search ?? '',
    });
  }, [
    isWeekly,
    isAdditional,
    appliedValues?.period_id,
    appliedValues?.month,
    appliedValues?.year,
    appliedValues?.search,
  ]);

  const yearOptions = getYearOptions();
  const initialFilter = isWeekly
    ? INITIAL_WEEKLY_FILTER
    : isAdditional
      ? INITIAL_ADDITIONAL_FILTER
      : INITIAL_MONTHLY_FILTER;
  const selectedPeriodName = filterFieldsData.period_id
    ? periodOptions.find((p) => p.id === String(filterFieldsData.period_id))?.name
    : '';

  const handleApply = () => {
    if (isWeekly) {
      applyFilter({
        period_id: filterFieldsData.period_id ?? '',
        search: filterFieldsData.search ?? '',
      });
      return;
    }
    applyFilter({
      period_id: filterFieldsData.period_id ?? '',
      month: filterFieldsData.period_id ? '' : filterFieldsData.month ?? '',
      year: filterFieldsData.period_id ? '' : filterFieldsData.year ?? '',
      search: filterFieldsData.search ?? '',
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleApply();
      }}
    >
      <DropDownInput
        label="Period"
        data={periodOptions}
        value={selectedPeriodName}
        getIdValue={(val) =>
          setFilterFieldsData((prev) => ({
            ...prev,
            period_id: val?.id ?? '',
            ...(isWeekly
              ? {}
              : {
                  month: '',
                  year: '',
                }),
          }))
        }
      />

      {!isWeekly && !filterFieldsData.period_id && (
        <>
          <DropDownInput
            label="Year"
            data={yearOptions}
            value={filterFieldsData.year}
            getIdValue={(val) =>
              setFilterFieldsData((prev) => ({ ...prev, year: val?.id ?? '' }))
            }
          />
          <DropDownInput
            label="Month"
            data={[...MONTHS]}
            value={
              filterFieldsData.month
                ? MONTHS.find((m) => m.id === filterFieldsData.month)?.name
                : ''
            }
            getIdValue={(val) =>
              setFilterFieldsData((prev) => ({ ...prev, month: val?.id ?? '' }))
            }
          />
        </>
      )}

      <ConstantInput
        label="Employee"
        type="text"
        placeholder="Search by employee name or email"
        value={filterFieldsData.search}
        handleChange={(value) =>
          setFilterFieldsData((prev) => ({
            ...prev,
            search: value?.trim?.() ?? value ?? '',
          }))
        }
      />
      <br />
      <br />
      <br />
      <div className="btn-wrapper">
        <button type="submit" className="btn btn-primary">
          {localization.applyFilter}
          <img src="../images/arrow-right.png" alt="" />
        </button>
        <br />
        <br />
        <button
          onClick={() => {
            setFilterFieldsData(initialFilter);
            applyFilter(initialFilter);
          }}
          type="button"
          className="btn btn-primary"
        >
          {localization.removeFilter}
        </button>
      </div>
    </form>
  );
};

SalesTargetsFilter.propTypes = {
  applyFilter: PropTypes.func.isRequired,
  listFilterData: PropTypes.shape({
    listType: PropTypes.string,
    appliedFilterValues: PropTypes.shape({
      period_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      month: PropTypes.string,
      year: PropTypes.string,
      search: PropTypes.string,
    }),
  }),
};

SalesTargetsFilter.defaultProps = {
  listFilterData: null,
};

export default SalesTargetsFilter;
